import React, { useState } from "react";
import styled from "styled-components";
import { Tag } from "@lifeat/shared/task-management/TaskTagTypes";
import { ValidateTagRequestResponse } from "@lifeat/shared/task-management/TaskTagUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { HorizontalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import { useToastListContext } from "@contexts/ToastListContext";
import TrashIconSvg from "@icons/TrashIcon.svg";
import Styles from "@themes/Styles";
import { darkTheme } from "@themes/Theme";
import { DeleteTagEvent, UpdateTagEvent } from "../AmplitudeTaskTagEvents";
import * as TaskListConstants from "../TaskListConstants";
import { useTaskListContext } from "../TaskListContext";
import { translateInputValidationError } from "../TaskManagementUtil";
import { TaskTagService } from "./../TaskTagService";
import TagDetailMenuContent from "./TagDetailMenuContent";

export const TagMenuFooter = styled.div`
    position: fixed;
    background-color: ${(props) => props.theme.backgroundPrimary};
    bottom: 0;
    width: 100%;
`;

export const TagActionButtonRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 5px;
`;

const DeleteIcon = styled(TrashIconSvg)`
    height: 16px;
    width: 16px;
    stroke: ${(props) => props.theme.textColorSpecial};
`;
const DeleteButtonLabel = styled.div`
    color: ${(props) => props.theme.textColorSpecial};
`;

const DELETE_TAG_LABEL = "Delete";
const SAVE_TAG_LABEL = "Save";

interface TagDetailMenuProps {
    readonly tagId: string;
    readonly tag: Tag;
    readonly validateTagName: (tagName: string) => ValidateTagRequestResponse;
    readonly handleClosePopup: () => void;
}
export default function TagDetailMenu(props: TagDetailMenuProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { addToastNotification } = useToastListContext();
    const { defaultTagId } = useTaskListContext();

    const [tagNameErrorMessage, setTagNameErrorMessage] = useState<string | undefined>();

    const [tagEmojiInputValue, setTagEmojiInputValue] = useState(props.tag.emoji);
    const [isTagDefault, setIsTagDefault] = useState(props.tagId === defaultTagId);
    const [tagColorInputValue, setTagColorInputValue] = useState(props.tag.color);
    const [tagNameInputValue, setTagNameInputValue] = useState(props.tag.name);

    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);

    const handleUpdateTagName = (updatedTagName: string) => {
        const validateTagNameResponse = props.validateTagName(updatedTagName);
        if (validateTagNameResponse.isValid === false && updatedTagName != props.tag.name) {
            setTagNameErrorMessage(translateInputValidationError(validateTagNameResponse.reason, updatedTagName));
        } else {
            setTagNameErrorMessage(undefined);
        }

        setTagNameInputValue(updatedTagName);
    };

    const handleDelete = () => {
        setIsDeleteLoading(true);
        TaskTagService.deleteTag(props.tagId).catch(() => {
            addToastNotification(TaskListConstants.TAG_DELETE_ERROR_TITLE, TaskListConstants.ERROR_TRY_AGAIN_MESSAGE);
        });

        amplitudeService.logEvent(new DeleteTagEvent({ id: props.tagId, name: props.tag.name }));
    };

    const handleSave = () => {
        setIsSaveLoading(true);
        const tagUpdateDiff = {
            tagId: props.tagId,
            ...(props.tag.name !== tagNameInputValue && { name: tagNameInputValue }),
            ...(props.tag.emoji !== tagEmojiInputValue && { emoji: tagEmojiInputValue }),
            ...(props.tag.color !== tagColorInputValue && { color: tagColorInputValue }),
            ...(((defaultTagId !== props.tagId && isTagDefault) || (defaultTagId === props.tagId && !isTagDefault)) && {
                isTagDefault: isTagDefault,
            }),
        };

        amplitudeService.logEvent(new UpdateTagEvent(tagUpdateDiff));

        TaskTagService.updateTag(tagUpdateDiff)
            .then(() => {
                props.handleClosePopup();
            })
            .catch(() => {
                addToastNotification(
                    TaskListConstants.TAG_UPDATE_ERROR_TITLE,
                    TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
                );
            });
    };

    const isSaveDisabled = () => {
        const noUpdatesToSave =
            props.tag.name === tagNameInputValue &&
            props.tag.color === tagColorInputValue &&
            props.tag.emoji === tagEmojiInputValue &&
            ((defaultTagId === props.tagId && isTagDefault) || (defaultTagId !== props.tagId && !isTagDefault));

        const tagNameUpdateIsInvalid =
            !props.validateTagName(tagNameInputValue).isValid && tagNameInputValue !== props.tag.name;

        return noUpdatesToSave || tagNameUpdateIsInvalid;
    };

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <TagDetailMenuContent
                tagEmojiInputValue={tagEmojiInputValue}
                isTagDefault={isTagDefault}
                tagColorInputValue={tagColorInputValue}
                tagNameInputValue={tagNameInputValue}
                handleUpdateEmoji={(updatedEmoji: string) => setTagEmojiInputValue(updatedEmoji)}
                handleUpdateColor={(updatedColor: string) => setTagColorInputValue(updatedColor)}
                handleUpdateDefault={(isTagDefault: boolean) => setIsTagDefault(isTagDefault)}
                handleUpdateTagName={handleUpdateTagName}
                tagNameErrorMessage={tagNameErrorMessage}
            />

            <TagMenuFooter>
                <HorizontalDivider widthCssValue={"100%"} />
                <TagActionButtonRow>
                    <LifeAtButtonV2 onClick={handleDelete} isLoading={isDeleteLoading}>
                        <DeleteIcon />
                        <DeleteButtonLabel>{DELETE_TAG_LABEL}</DeleteButtonLabel>
                    </LifeAtButtonV2>
                    <LifeAtButtonV2
                        onClick={handleSave}
                        isDisabled={isSaveDisabled()}
                        variant={"solid"}
                        overrideBackgroundColor={Styles.buttonFillBlue}
                        overrideTextColor={darkTheme.textColorPrimary}
                        disableHover={true}
                        isLoading={isSaveLoading}
                    >
                        {SAVE_TAG_LABEL}
                    </LifeAtButtonV2>
                </TagActionButtonRow>
            </TagMenuFooter>
        </div>
    );
}
